import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc } from '@/trpcClient';
import { checkTransferMaterialInput, TransferMaterialInputType } from '@shelflix/schemas/material/transfer';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, Building2, MapPin } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface TransferSectionProps {
  materialUuid: string;
  facility: {
    uuid: string;
    name: string;
    address: string;
    city: {
      uuid: string;
      name: string;
      ibgeCode: string;
    };
    state: {
      uuid: string;
      name: string;
      ibgeCode: string;
      stateName: string;
    };
  };
}

export function TransferSection({ materialUuid, facility }: TransferSectionProps) {
  const transferMaterialMutation = useMutation(trpc.material.transfer.mutationOptions());

  const { data, isPending } = useQuery(trpc.facility.list.queryOptions({ recordsPerPage: 9999 }));

  const form = useForm<TransferMaterialInputType>({
    defaultValues: {
      materialUuid: materialUuid,
      targetFacilityUuid: '',
      comments: '',
    },
    mode: 'all',
  });

  const onSubmit = (data: TransferMaterialInputType) => {
    if (transferMaterialMutation.isPending) return;
    transferMaterialMutation.mutate(data, {
      onSuccess: (result) => {
        toast.success(result.message);
        form.reset();
        window.location.reload();
      },
      onError: (error) => {
        toast.error(`Erro ao transferir material: ${error.message}`);
      },
    });
  };

  return (
    <Card className="rounded-lg border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
          <ArrowRightLeft className="h-5 w-5" />
          Transferir Material
        </CardTitle>
        <p className="mb-3 text-sm">
          Transfira este material para outro estabelecimento.
          <br />
          Todas as campanhas e configurações serão mantidas.
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-6 rounded-lg bg-[var(--background)] p-4">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 text-gray-500" />
            <div>
              <h4 className="font-medium">Estabelecimento Atual</h4>
              <div className="mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {facility.name} - {facility.city.name} / {facility.state.name}
                </span>
              </div>
            </div>
          </div>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="targetFacilityUuid"
              rules={{ validate: (value) => checkTransferMaterialInput('targetFacilityUuid', value) }}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="flex items-start gap-3 text-sm font-medium text-[var(--gray-primary)]">
                    <Building2 className="h-5 w-5 text-gray-500" />
                    <div className="flex flex-col items-start">
                      <span>Estabelecimento de Destino</span>
                    </div>
                  </FormLabel>
                  <FormControl className="">
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={transferMaterialMutation.isPending || isPending}
                    >
                      <SelectTrigger className="w-full bg-amber-300">
                        <SelectValue
                          placeholder={
                            isPending ? 'Buscando estabelecimentos...' : 'Selecione o estabelecimento de destino'
                          }
                          className="bg-amber-300"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {data?.facilities
                          ?.filter((fac) => fac.uuid !== facility.uuid) // Excluir o estabelecimento atual
                          .map((facility) => (
                            <SelectItem key={facility.uuid} value={facility.uuid}>
                              <div className="flex flex-col items-start">
                                <span className="font-medium text-[var(--gray-primary)]">{facility.name}</span>
                                <span className="text-xs">
                                  {facility.city?.name} / {facility.state?.name}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="text-sm font-medium text-[var(--gray-primary)]">
                    Comentários (opcional)
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Adicione comentários sobre a transferência..."
                      disabled={transferMaterialMutation.isPending}
                      className="min-h-[100px] resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="rounded-lg border border-amber-200 bg-[var(--background)] p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div>
                  <h4 className="font-medium">Atenção</h4>
                  <ul className="mt-1 space-y-1 text-sm">
                    <li>• Esta operação moverá o dispositivo para o novo estabelecimento</li>
                    <li>• Todas as campanhas agendadas serão mantidas</li>
                    <li>• O material será removido do estabelecimento atual</li>
                    <li>• Esta ação não pode ser desfeita facilmente</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <RestrictedComponent
                requiredScopes={['material:transfer']}
                behavior="disable"
                showToast
                toastMessage="Você não tem permissão para transferir materiais."
              >
                <Button
                  type="submit"
                  loading={transferMaterialMutation.isPending}
                  disabled={!form.watch('targetFacilityUuid') || transferMaterialMutation.isPending}
                  className="bg-[var(--primary)] px-6 hover:bg-[var(--primary)]"
                >
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                  {transferMaterialMutation.isPending ? 'Transferindo...' : 'Transferir Material'}
                </Button>
              </RestrictedComponent>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
